'use client';

import { FC, ReactNode, useRef, useState } from 'react';
import { Settings } from 'react-slick';
import { twMerge } from 'tailwind-merge';

import Slider from '@/features/SlickSliderWrapper';
import Svg from '@/components/Svg';
import { ChevronRightIcon } from '@/icons';
import ProgressBar from '@/features/Account/components/ProgressBar';

type ClassesTypes = {
  arrows?: {
    root?: string;
    prev?: string;
    next?: string;
    color?: string;
  };
  progress?: {
    slide?: string;
    root?: string;
  };
  root?: string;
  reactSlick?: string;
};

interface CarouselPropTypes {
  children: any;
  settings?: Settings & { progress?: boolean };
  classes?: ClassesTypes;
}

type ArrowButtonsProps = {
  children: ReactNode;
  className: string;
  onClick?: any;
  color?: string;
};

const ArrowButtons = ({
  children,
  className,
  onClick,
  color,
}: ArrowButtonsProps) => (
  <button
    onClick={(e: any) => {
      if (onClick) onClick(e);
      e.preventDefault();
      e.stopPropagation();
    }}
    className={twMerge(
      `absolute top-2/4 z-1 -translate-y-2/4 rounded-full`,
      className,
      !onClick && 'cursor-not-allowed opacity-50'
    )}>
    <Svg
      className={`h-5 w-5 fill-none stroke-${color ?? 'fig'} stroke-[1.5]`}
      viewBox='0 0 24 24'>
      {children}
    </Svg>
  </button>
);

const Carousel: FC<CarouselPropTypes> = ({ settings, children, classes }) => {
  const PreviousArrow = ({ onClick }: any) => (
    <ArrowButtons
      className={twMerge(
        `-left-1 -rotate-180 lg:-left-4 xl:-left-6`,
        classes?.arrows?.root,
        classes?.arrows?.prev
      )}
      color={classes?.arrows?.color}
      onClick={onClick}>
      <ChevronRightIcon />
    </ArrowButtons>
  );

  const NextArrow = ({ onClick }: any) => (
    <ArrowButtons
      className={twMerge(
        `-right-1 lg:-right-4 xl:-right-6`,
        classes?.arrows?.root,
        classes?.arrows?.next
      )}
      color={classes?.arrows?.color}
      onClick={onClick}>
      <ChevronRightIcon />
    </ArrowButtons>
  );

  const BASE_SETTINGS: Settings = {
    slidesToShow: 4,
    slidesToScroll: 1,
    dots: false,
    infinite: true,
    pauseOnHover: true,
    pauseOnFocus: true,
    autoplaySpeed: 3000,
    speed: 250,
    arrows: true,
    nextArrow: settings?.arrows ? <NextArrow /> : <></>,
    prevArrow: settings?.arrows ? <PreviousArrow /> : <></>,
    initialSlide: 0,
    onSwipe: () => document.getElementById('searchBar')?.blur(),
    swipe: true,
    swipeToSlide: true,
    draggable: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          initialSlide: 0,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          initialSlide: 2,
        },
      },
    ],
    ...settings,
  };

  const infinite = children.length === 1 ? false : BASE_SETTINGS.infinite;
  BASE_SETTINGS.infinite = infinite;

  const ref = useRef<any>(null);
  const [progress, setProgress] = useState<number>(0);
  const onChange = (_current: number, next: number) => {
    setProgress(next);
  };

  return (
    <div
      aria-label='carousel'
      className={twMerge('space-y-4 md:space-y-6', classes?.root)}>
      <Slider
        {...BASE_SETTINGS}
        ref={ref}
        beforeChange={onChange}
        className={classes?.reactSlick}>
        {children}
      </Slider>
      {(settings?.progress ?? true) && (
        <div className='mx-auto w-24'>
          <ProgressBar
            progress={progress + 1}
            total={children.length}
            classNames={classes?.progress}
          />
        </div>
      )}
    </div>
  );
};

export default Carousel;
